import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useNavigate} from '@github-ui/use-navigate'
import {useUser} from '@github-ui/use-user'
import {reactFetchJSON, verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useEffect, useState} from 'react'

type SemanticSearchFeaturePreview = {
  isAvailable: boolean
  isEnabled: boolean
  isLoading: boolean
  feedbackLink?: string
  toggle: () => Promise<void>
}

type FeaturePreviewResponse = {
  features: Array<{slug: string; enrolled: boolean; feedback_link?: string}>
}

const useIssuesIndexSemanticSearchToggleFeaturePreview: () => SemanticSearchFeaturePreview = () => {
  const {currentUser} = useUser() ?? {}
  const login = currentUser?.login ?? ''
  const navigate = useNavigate()
  const isAvailable = isFeatureEnabled('issues_index_semantic_search')

  const [data, setData] = useState<FeaturePreviewResponse | undefined>(undefined)
  const shouldFetch = isAvailable && !!login

  useEffect(() => {
    if (!shouldFetch) {
      return
    }

    let cancelled = false

    const fetchData = async () => {
      try {
        const response = await reactFetchJSON(`/users/${login}/feature_previews`)
        const json = (await response.json()) as FeaturePreviewResponse
        if (!cancelled) {
          setData(json)
        }
      } catch {
        // Handle error silently
      }
    }
    void fetchData()
    return () => {
      // Cleanup to avoid setting state on unmounted component
      cancelled = true
    }
  }, [shouldFetch, login])

  const isLoading = shouldFetch && !data
  const feature = data?.features.find(f => f.slug === 'issues_index_semantic_search')
  const isEnabled = feature?.enrolled ?? false
  const feedbackLink = feature?.feedback_link

  const toggle = useCallback(async () => {
    await verifiedFetchJSON('/feature_preview/issues_index_semantic_search/toggle', {
      method: 'POST',
    })

    // Short delay to give feature preview time to kick in
    setTimeout(() => {
      navigate('')
    }, 250)
  }, [navigate])

  return {isAvailable, isEnabled, isLoading, feedbackLink, toggle}
}

export {useIssuesIndexSemanticSearchToggleFeaturePreview}
export type {SemanticSearchFeaturePreview}
